import { AuthService } from './../../../../base/services/auth.service';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControlOptions, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { SearchSettingsModel, GridModule } from '@syncfusion/ej2-angular-grids';
import { ExportInterfaceCurrency, IExportInterfaceFieldAssignmentDTO } from 'src/app/base/models/models.swagger.generated';
import { GridLocalDataBaseComponent } from 'src/app/base/shared/grid-base/grid-local-data-base.component';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { FieldAssignmentsFormValidators } from './field-assignments-form.validators';
import { FieldAssignmentEntryFormComponent } from './field-assignment-entry-form/field-assignment-entry-form.component';
import { QueryBuilderModule } from '@syncfusion/ej2-angular-querybuilder';

@Component({
    selector: 'app-field-assignments-form',
    templateUrl: './field-assignments-form.component.html',
    styleUrls: ['./field-assignments-form.component.scss'],
    imports: [GridModule, QueryBuilderModule, FieldAssignmentEntryFormComponent, TranslateModule]
})
export class FieldAssignmentsFormComponent extends GridLocalDataBaseComponent {
  @Input()
  public showTemplateButtons: boolean = true;

  @Input()
  public set fieldAssignmentsForm(form: UntypedFormGroup) {
    if (this._fieldAssignmentsForm != form) {
      this._fieldAssignmentsForm = form;
      this.initData();
    }
  }

  @Output()
  public loadFromTemplateClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public saveAsTemplateClicked: EventEmitter<void> = new EventEmitter();

  public get fieldAssignmentsForm(): UntypedFormGroup {
    return this._fieldAssignmentsForm;
  }

  public get canEditConfiguration(): boolean {
    return this.authService.canUserEditExportConfigurations();
  }

  public get canShowSaveButton(): boolean {
    return this.showTemplateButtons && this.authService.isUserAdmin();
  }

  public checkboxFilter = GridConstantsFactory.createCheckBoxFilter();
  public selectOptions = { type: 'Single' };

  private _fieldAssignmentsForm: UntypedFormGroup;

  constructor(
    translateService: TranslateService,
    private fb: UntypedFormBuilder,
    private authService: AuthService
  ) {
    super(null, translateService, null);
    this.gridCommands = GridConstantsFactory.createStandardGridCommands();
    this.searchOptions = { fields: ['source', 'target'], operator: 'startswith' };
  }

  public getCurrencyString(currency: ExportInterfaceCurrency): string {
    if (currency != null) {
      return 'i18n.shared.currencies.' + ExportInterfaceCurrency[currency];
    }
    return null;
  }

  public formatNullValueFilter(data: any): string {
    return data && data.exportZeroValueAsEmpty ? 'i18n.shared.yes' : 'i18n.shared.no';
  }

  public override actionComplete(args) {
    super.actionComplete(args);
    if (args.requestType === 'delete') {
      const rowsLeft = args.rows?.length ?? 0;
      if (rowsLeft <= 0) {
        this.fieldAssignmentsForm.get('configurationType')?.enable();
      }
    }
  }

  // overridden methods for grid component  ***************
  protected initializeEditForm(data: any): void {
    this.gridEditForm = this.fb.group({
      sourceName: [data.sourceName],
      targetName: [data.targetName],
      overwriteCurrency: [data.overwriteCurrency],
      fixValue: [data.fixValue],
      cap: [data.cap],
      exportZeroValueAsEmpty: [data.exportZeroValueAsEmpty ?? false],
      customField1Value: [data.customField1Value],
      customField2Value: [data.customField2Value],
      customField3Value: [data.customField3Value],
      customField4Value: [data.customField4Value],
      customField5Value: [data.customField5Value]
    },
    {
      validators: [
                    FieldAssignmentsFormValidators.sourceTargetValidator,
                    FieldAssignmentsFormValidators.noFixAndCapValidator,
                  ]
    } as AbstractControlOptions);
  }

  protected getEditDialogTitleParams(rowData: any): object {
    return { param: rowData.sourceName };
  }

  protected override getDataToSave(data: any): any {
    const dat = data as IExportInterfaceFieldAssignmentDTO;
    dat.sourceName = this.gridEditForm.value.sourceName;
    dat.targetName = this.gridEditForm.value.targetName;
    dat.overwriteCurrency = this.gridEditForm.value.overwriteCurrency;
    dat.fixValue = this.gridEditForm.value.fixValue;
    dat.cap = this.gridEditForm.value.cap;
    dat.exportZeroValueAsEmpty = this.gridEditForm.value.exportZeroValueAsEmpty;
    dat.customField1Value = this.gridEditForm.value.customField1Value;
    dat.customField2Value = this.gridEditForm.value.customField2Value;
    dat.customField3Value = this.gridEditForm.value.customField3Value;
    dat.customField4Value = this.gridEditForm.value.customField4Value;
    dat.customField5Value = this.gridEditForm.value.customField5Value;
  }

  protected override dataSaveCompleteAction(args: any) {
    if (args.action === 'edit') {
      const fieldscontrol = this.fieldAssignmentsForm.get('assignments');
      if (args.rowIndex >= 0 && fieldscontrol.value?.length > args.rowIndex) {
        fieldscontrol.value[args.rowIndex] = args.data;
        this.grid.refresh();
      }
    }
    if (args.action === 'add') {
      this.fieldAssignmentsForm.get('configurationType')?.disable();
    }
  }
  // overridden methods for grid component  ***************

  public onAddClick() {
    this.grid.addRecord();
  }

  public loadFromTemplate() {
    this.loadFromTemplateClicked.emit();
  }

  public saveToTemplate() {
    this.saveAsTemplateClicked.emit();
  }

  public refresh() {
    this.initData();
  }

  private initData(): void {
    //Sort by position
    this.data = this._fieldAssignmentsForm.get('assignments').value?.sort((a, b) => a.position > b.position ? 1 : -1);
    this.isActiveColumnName = 'active';
    this.editDialogTitle = 'i18n.domain.export-configuration.configurations.forms.file.field-configuration.form-fields.edit-dialog-title';
    this.addDialogTitle = 'i18n.domain.export-configuration.configurations.forms.file.field-configuration.form-fields.add-dialog-title';

    this.editSettings = { ...this.editSettings, showDeleteConfirmDialog: false };
  }
}
